/**
 * 特殊記号を含むかのチェックはUnicodeで管理している
 * APIで行っている禁止文字チェックで弾いていない文字も含まれるため注意
 * iweb連携時のバリデーションチェックを考慮して以下のバリデーションチェックは残してある
 * - サロゲートペア文字の範囲から特殊記号のみ
 * - 結合記号: ◌̀, ◌́, ◌̂, ◌̃, ◌̄
 * - 通貨記号: €, ₡, ₢, ₣, ₤
 * - 類似文字: ™, ℠, ℡, ℣, ℤ
 * - 数学記号: ∀, ∁, ∂, ∃, ∄
 * - ブロック要素: ▀, ▁, ▂, ▃, ▄
 * - 罫線図形: ┄
 * - 幾何学的図形: ◢, ◣, ◤, ◥, ◦
 * - 天気など記号: ☀, ☁, ☂, ☃, ☄
 */
export const includesUnsupportedSymbols = (input?: string): boolean => {
  if (!input) return false;
  // ローマ数字
  const romanNumbersPattern = /[\u2160-\u216B\u2170-\u217B]/;

  // サロゲートペア文字の範囲から特殊記号のみ
  const surrogatePairPattern = /(?:[\uD83C\uD83D\uD83E][\uDC00-\uDFFF]|[\uD834][\uDD00-\uDDFF])/;

  // 結合記号: ◌̀, ◌́, ◌̂, ◌̃, ◌̄
  const combiningMarksPattern = /[\u0300-\u036F\u1AB0-\u1AFF\u1DC0-\u1DFF\u20D0-\u20FF]/;

  // 通貨記号: €, ₡, ₢, ₣, ₤
  const currencySymbolsPattern = /[\u20A0-\u20CF]/;

  // 類似文字: ™, ℠, ℡, ℣, ℤ
  const letterlikeSymbolsPattern = /[\u2100-\u214F]/;

  // 数学記号: ∀, ∁, ∂, ∃, ∄
  const mathSymbolsPattern = /[\u2200-\u22FF\u27C0-\u27EF\u2980-\u29FF]/;

  // 矢印記号: いまのところ矢印記号は許可している
  // const arrowSymbolsPattern = /[\u2190-\u21FF]/;

  // ブロック要素: ▀, ▁, ▂, ▃, ▄
  const blockElementsPattern = /[\u2580-\u259F]/;

  // 罫線図形: ┄
  const boxDrawingPattern = /[\u2504]/;

  // 幾何学的図形: ◢, ◣, ◤, ◥, ◦
  const geometricShapesPattern = /[\u25A2-\u25B1\u25B4-\u25BB\u25BE-\u25C5\u25C8-\u25CA\u25CC-\u25CE\u25D0-\u25FF]/;

  // 天気など記号: ☀, ☁, ☂, ☃, ☄
  const miscellaneousSymbolsPattern = /[\u2607-\u26FF]/;

  // 略語: ㈱ ㈲ ㈹ ㍿ ㍾ ㍽ ㍼ ㍻ ㋿
  const abbreviationPattern = /[\u3231-\u3243\u32FF\u337a-\u337f]/;

  // 単位記号: [㌀,㌁...㍖,㍗],[㎀,㎁...㏜,㏝]
  const unitSymbolsPattern = /[\u3300-\u3357\u3371-\u3376\u3380-\u33DD]/;

  const patterns = [
    romanNumbersPattern,
    surrogatePairPattern,
    combiningMarksPattern,
    currencySymbolsPattern,
    letterlikeSymbolsPattern,
    mathSymbolsPattern,
    blockElementsPattern,
    boxDrawingPattern,
    geometricShapesPattern,
    miscellaneousSymbolsPattern,
    abbreviationPattern,
    unitSymbolsPattern
  ];

  return patterns.some(pattern => pattern.test(input));
};

export const includesEmoji = (input?: string): boolean => {
  if (!input) return false;

  // Emoji・Emoji_Component
  // c.f. https://www.unicode.org/reports/tr51/#Emoji_Properties
  // ASCII
  // c.f. https://unicode.org/reports/tr18/#General_Category_Property
  // Emojiだと数値もチェックされてしまうため、否定先読みでASCII文字は除外
  // c.f. https://stackoverflow.com/questions/64389323/why-do-unicode-emoji-property-escapes-match-numbers
  const emojiRegex = /(?!\p{ASCII})[\p{Emoji}\p{Emoji_Component}]/u;

  const arrowRegex = /[^\u2194\u2195]/;

  const patterns = [emojiRegex, arrowRegex];

  return patterns.every(pattern => pattern.test(input));
};
